// Constant-tempo click grid fitted to the detected beats — one tempo per block (pure).
//
// A rounded header BPM drifts audibly over a whole song (0.5 BPM ≈ 1.5 beats over 4 min), so each
// block's period and phase come from a least-squares line through its beat times: t_k ≈ t0 + k·P
// (k = global beat index). Songs whose tempo really changes (a slower bridge, a ritardando ending,
// a live take) get several blocks:
//   1. optimal partition into blocks of ≥ MIN_BARS (4) bars (dynamic programming over bar boundaries,
//      cost = squared residual / σ² + a per-block penalty; σ = beat jitter estimated from the data)
//   2. merge neighbours while ONE tempo would still sound right: on EACH side 90% of beats within
//      30 ms of the merged grid. So a steady song stays a single block, and blocks only appear where a
//      single tempo would audibly drift or a beat slips (tiny tempo differences get no marker on screen).
//   3. a block (other than the last) at a 3:2 / 4:3 / 2:1 tempo ratio to the main one is the tracker
//      switching beat level, not a real tempo change — it keeps its neighbour's grid

import type { ChartMeasure } from "../../types/domain";

export interface TempoSegment { startBar: number; endBar: number; period: number; bpm: number }
export interface FixedGrid {
  period: number;          // seconds per beat of the longest block
  bpm: number;             // 60 / period (unrounded)
  beatsPerBar: number;     // 3 or 4 (dominant bar length in the chart)
  segments: TempoSegment[];   // blocks in song order (endBar exclusive)
  clicks: { t: number; accent: boolean }[];
}

const MIN_BARS = 4;
const AUDIBLE = 0.03;      // s: drift a listener notices against the song
const RATIOS = [2 / 3, 3 / 2, 3 / 4, 4 / 3, 1 / 2, 2];   // beat-level slips, not tempo changes

interface Line { t0: number; P: number; sse: number }

export function fitFixedGrid(measures: ChartMeasure[]): FixedGrid | null {
  const t: number[] = [];
  const barFirst: number[] = [];               // global beat index of each bar's first beat
  for (const m of measures) { barFirst.push(t.length); for (const b of m.beats) t.push(b.time); }
  const n = t.length, M = measures.length;
  barFirst.push(n);
  if (n < 4) return null;

  // prefix sums for O(1) least-squares fits over any beat range [i0, i1)
  const S = { x: [0], y: [0], xx: [0], xy: [0], yy: [0] };
  for (let i = 0; i < n; i++) {
    S.x.push(S.x[i] + i); S.y.push(S.y[i] + t[i]); S.xx.push(S.xx[i] + i * i);
    S.xy.push(S.xy[i] + i * t[i]); S.yy.push(S.yy[i] + t[i] * t[i]);
  }
  const fit = (i0: number, i1: number): Line | null => {
    const k = i1 - i0;
    if (k < 2) return null;
    const sx = S.x[i1] - S.x[i0], sy = S.y[i1] - S.y[i0], sxx = S.xx[i1] - S.xx[i0];
    const sxy = S.xy[i1] - S.xy[i0], syy = S.yy[i1] - S.yy[i0];
    const den = k * sxx - sx * sx;
    if (den <= 0) return null;
    const P = (k * sxy - sx * sy) / den;
    const t0 = (sy - P * sx) / k;
    const sse = Math.max(0, syy - sy * sy / k - P * (sxy - sx * sy / k));
    return { t0, P, sse };
  };
  const whole = fit(0, n);
  if (!whole || !(whole.P > 0.15 && whole.P < 2.5)) return null;

  // bar length = most common measure length; 3 stays 3, everything else is treated as 4
  const counts: Record<number, number> = {};
  for (const m of measures) counts[m.beats.length] = (counts[m.beats.length] || 0) + 1;
  let dom = 4, best = -1;
  for (const k in counts) if (counts[k] > best) { best = counts[k]; dom = +k; }
  const beatsPerBar = dom === 3 ? 3 : 4;

  // 1. optimal partition (bars) — skipped for songs too short to hold two blocks
  let cuts = [0, M];
  if (M >= 2 * MIN_BARS) {
    const d2: number[] = [];
    for (let i = 1; i + 1 < n; i++) d2.push(Math.abs(t[i + 1] - 2 * t[i] + t[i - 1]));
    d2.sort((a, b) => a - b);
    const sigma = Math.max(0.005, (1.4826 * d2[Math.floor(d2.length / 2)]) / Math.sqrt(6));
    const penalty = 12 * Math.log(n);
    const dp = new Array<number>(M + 1).fill(Infinity), from = new Array<number>(M + 1).fill(-1);
    dp[0] = 0;
    for (let j = MIN_BARS; j <= M; j++) {
      for (let i = 0; i <= j - MIN_BARS; i++) {
        if (dp[i] === Infinity || (i > 0 && i < MIN_BARS)) continue;
        const f = fit(barFirst[i], barFirst[j]);
        if (!f) continue;
        const c = dp[i] + f.sse / (sigma * sigma) + penalty;
        if (c < dp[j]) { dp[j] = c; from[j] = i; }
      }
    }
    if (dp[M] < Infinity) {
      const cs: number[] = [M];
      for (let j = M; j > 0; j = from[j]) cs.push(from[j]);
      cuts = cs.reverse();
    }
  }

  // 2. merge neighbours while a single tempo still sounds right
  const p90 = (a: number, b: number, f: Line) => {
    const r: number[] = [];
    for (let i = barFirst[a]; i < barFirst[b]; i++) r.push(Math.abs(t[i] - (f.t0 + i * f.P)));
    r.sort((x, y) => x - y);
    return r.length ? r[Math.floor(r.length * 0.9)] : 0;
  };
  for (;;) {
    let bestK = -1, bestScore = Infinity;
    for (let k = 1; k + 1 < cuts.length; k++) {
      const a = cuts[k - 1], m = cuts[k], b = cuts[k + 1];
      const L = fit(barFirst[a], barFirst[m]), R = fit(barFirst[m], barFirst[b]), J = fit(barFirst[a], barFirst[b]);
      if (!L || !R || !J) { bestK = k; bestScore = -1; break; }
      // judge each side on its own — a short block's drift must not hide in a long block's 10% tail
      const drift = Math.max(p90(a, m, J), p90(m, b, J));
      if (drift <= AUDIBLE && drift < bestScore) { bestScore = drift; bestK = k; }
    }
    if (bestK < 0) break;
    cuts.splice(bestK, 1);
  }

  // 3. per block: its line, bar length (the bars the grid shows) and accent phase (first full-length bar)
  interface Block { a: number; b: number; line: Line; segBar: number; anchorK: number; barLen: number }
  const blocks: Block[] = [];
  for (let s = 0; s + 1 < cuts.length; s++) {
    const a = cuts[s], b = cuts[s + 1];
    const line = fit(barFirst[a], barFirst[b]) ?? whole;
    const segCounts: Record<number, number> = {};
    for (let m = a; m < b; m++) segCounts[measures[m].beats.length] = (segCounts[measures[m].beats.length] || 0) + 1;
    let segBar = beatsPerBar, segBest = -1;
    for (const k in segCounts) if (segCounts[k] > segBest && +k >= 2 && +k <= 8) { segBest = segCounts[k]; segBar = +k; }
    let full = a;
    while (full < b && measures[full].beats.length !== segBar) full++;
    const anchorK = Math.round((measures[full < b ? full : a].startTime - line.t0) / line.P);
    const lens = measures.slice(a, b).map((m) => m.endTime - m.startTime).sort((x, y) => x - y);
    blocks.push({ a, b, line, segBar, anchorK, barLen: lens[Math.floor(lens.length / 2)] });
  }

  // 4. a block whose beats run at a 3:2 / 4:3 / 2:1 relative of the main tempo while its BARS keep the
  //    main bar length is the tracker counting a different subdivision, not a tempo change (checked by
  //    ear on the cached songs) — it continues its neighbour's grid. Blocks whose bars change length too
  //    are left alone, and so is the last block (a song really can slow down at its end).
  const longest = blocks.reduce((x, y) => (y.b - y.a > x.b - x.a ? y : x));
  const slip = (bl: Block) => bl !== longest && bl !== blocks[blocks.length - 1]
    && RATIOS.some((r) => Math.abs(longest.line.P / bl.line.P - r) < r * 0.04)
    && Math.abs(bl.barLen - longest.barLen) <= longest.barLen * 0.08;
  for (let i = 0; i < blocks.length; i++) {
    if (!slip(blocks[i])) continue;
    let j = i - 1;
    while (j >= 0 && slip(blocks[j])) j--;
    if (j < 0) { j = i + 1; while (j < blocks.length && slip(blocks[j])) j++; }
    const src = blocks[j] ?? longest;
    blocks[i] = { ...blocks[i], line: src.line, segBar: src.segBar, anchorK: src.anchorK };
  }
  // neighbours now on the same line are one block
  const merged: Block[] = [];
  for (const bl of blocks) {
    const last = merged[merged.length - 1];
    if (last && last.line === bl.line) last.b = bl.b;
    else merged.push({ ...bl });
  }

  // 5. clicks, by time on each block's line; switch at the block's first downbeat, run back to t ≥ 0
  //    before the first block and on to the end after the last
  const segments: TempoSegment[] = [];
  const clicks: FixedGrid["clicks"] = [];
  const end = measures[M - 1].endTime;
  merged.forEach((bl, i) => {
    const { t0, P } = bl.line;
    segments.push({ startBar: bl.a, endBar: bl.b, period: P, bpm: 60 / P });
    const firstK = i === 0 ? Math.ceil(-t0 / P) : Math.ceil((measures[bl.a].startTime - t0) / P - 0.25);
    const stopT = i === merged.length - 1 ? end + 1e-6 : measures[bl.b].startTime - P * 0.25;
    for (let k = firstK; t0 + k * P < stopT; k++) {
      const tk = t0 + k * P;
      if (tk < 0) continue;
      if (clicks.length && tk <= clicks[clicks.length - 1].t + P * 0.25) continue;   // no doubled click at a seam
      clicks.push({ t: tk, accent: (((k - bl.anchorK) % bl.segBar) + bl.segBar) % bl.segBar === 0 });
    }
  });
  const main = segments.reduce((x, y) => (y.endBar - y.startBar > x.endBar - x.startBar ? y : x));
  return { period: main.period, bpm: main.bpm, beatsPerBar, segments, clicks };
}

/**
 * Tempo changes worth showing: the first block, then every block whose BPM moved by ≥ 2 from the last
 * shown one. Blocks that only re-lock the phase at the same tempo stay silent (no marker, no count).
 */
export function tempoChanges(grid: FixedGrid | null): { bar: number; bpm: number }[] {
  if (!grid) return [];
  const out: { bar: number; bpm: number }[] = [];
  for (const s of grid.segments) {
    const bpm = Math.round(s.bpm);
    if (!out.length || Math.abs(bpm - out[out.length - 1].bpm) >= 2) out.push({ bar: s.startBar, bpm });
  }
  return out;
}
